import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import EmailCapturePopup from '../components/EmailCapturePopup';
import TestimonialsSection from '../components/TestimonialsSection';
import { optimizeImage } from '../utils/cloudinary';
import '../styles/testimonials.css';

const LandingPage = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hero image - Cloudinary optimized (no cropping, just compression + format)
  const heroImage = optimizeImage(
    'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/4xm6s175_4H4A9423.JPG',
    { width: 1920 }
  );

  const services = [
    {
      id: 'bungee',
      title: 'Bungee Fitness',
      subtitle: 'Flight',
      description: 'Low-impact bungee exercise classes. High cardio, happy joints. Adults + kids welcome.',
      promo: 'First time? BOGO — two 101 classes for $35',
      image: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/fus649mk_DSC_1830.jpg', { width: 800 }),
      href: '/bungee'
    },
    {
      id: 'aerial',
      title: 'Aerial Kids',
      subtitle: 'Play',
      description: 'Weekly aerial classes + adventure camps in Wilmington, NC. Screen-free fun that builds confidence.',
      image: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/lp16f1ek_Sara_Aerial.JPG', { width: 800 }),
      href: '/aerial'
    },
    {
      id: 'workouts',
      title: 'Start Where You Are',
      subtitle: 'Strength',
      description: 'Simple strength exercises you can do from anywhere. Build a workout in 7 seconds. Mostly bodyweight, no equipment, no overthinking.',
      image: '/images/landing/strength_tile.jpg',
      href: '/workouts'
    }
  ];

  return (
    <div className="landing-page">
      {/* Email Capture Popup - shows after 5 seconds */}
      <EmailCapturePopup delay={5000} />
      
      {/* ① HERO Section with Single Image */}
      <section className="hero-section hero-single-image">
        {/* Background Image with Fade */}
        <div className="hero-bg-image">
          <img 
            src={heroImage}
            alt="Bungee fitness class - pure joy in motion"
            className="hero-image"
          />
          <div className="hero-image-overlay"></div>
        </div>
        
        <div className="hero-content">
          <div 
            className="hero-text"
            style={{ 
              transform: `translateY(${scrollY * 0.3}px)`,
              opacity: 1 - scrollY / 500 
            }}
          >
            <h1 className="hero-title-clean">
              Fitness that feels like <span className="play-emphasis">play</span>
            </h1>
            <p className="hero-subtitle">
              Bungee fitness + Kids aerial adventures. Wilmington, NC.
            </p>
            <p className="hero-tagline">Serious movement. Serious play.</p>
            <div className="hero-cta">
              <Link to="/book-bungee">
                <Button size="lg" className="cta-primary">
                  Book a Bungee Class
                </Button>
              </Link>
              <Link to="/book-aerial">
                <Button size="lg" variant="outline" className="cta-secondary-light">
                  Kids Classes & Camps
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
          </div>
          
          <div className="scroll-indicator">
            <ChevronDown className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* ② BRIDGE LINE - Below Hero */}
      <section className="bridge-section bridge-compact">
        <p className="bridge-tagline">Be your own kind of strong.</p>
      </section>

      {/* Featured In Section */}
      <section className="featured-in-section">
        <p className="featured-label">As featured in</p>
        <p className="featured-name">Cape Fear Community College</p>
        <p className="featured-subtitle">Small Business Center Spotlight</p>
        <a 
          href="https://cfcc.edu/small-business-center-spotlight-cal-cutler-turned-bungee-fitness-into-gocalfit/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="featured-link"
        >
          Read the story →
        </a>
      </section>

      {/* ③ Services Grid - "Three ways to fly" */}
      <section className="services-section">
        <div className="services-intro">
          <h2 className="section-title">Three Paths</h2>
          <p className="section-subtitle">One Journey</p>
        </div>

        <div className="services-grid">
          {services.map((service, index) => {
            return (
              <Link 
                key={service.id} 
                to={service.href}
                className="service-card service-card-with-image"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="service-card-glow"></div>
                <div className="service-card-image-container">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="service-card-image"
                  />
                </div>
                <div className="service-card-content">
                  <div className="service-subtitle-badge">{service.subtitle}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  {service.promo && (
                    <p className="service-promo" data-testid="bungee-card-bogo">{service.promo}</p>
                  )}
                  <div className="service-cta">
                    Discover More
                    <span className="arrow">→</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        
        {/* Your Move - Below tiles */}
        <p className="your-move-text"><strong>Your Move.</strong></p>
      </section>

      {/* ④ WHISPER TEASE - Between Tiles + Reviews */}
      <section className="whisper-section">
        <div className="whisper-content">
          <h3 className="whisper-title">Not ready to fly yet? That's okay.</h3>
          <p className="whisper-text">
            We'll come to you.
          </p>
          <Link to="/consulting#contact-form" className="whisper-link">
            Stay in the Loop
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ⑤ REVIEWS - Updated Header */}
      <TestimonialsSection />

      {/* ⑥ FOOTER CTA STRIP */}
      <section className="final-cta-section">
        <div className="final-cta-content">
          <h2 className="final-cta-title">Ready to fly?</h2>
          <p className="final-cta-text">You found the right place.</p>
          <div className="final-cta-buttons">
            <Link to="/book-bungee">
              <Button size="lg" className="cta-final">
                Book a Bungee Class
              </Button>
            </Link>
            <Link to="/book-aerial">
              <Button size="lg" variant="outline" className="cta-final-secondary">
                Kids Camps & Classes
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
